import React from "react";
import { ShieldCheck, FileText, ScrollText } from "lucide-react";
import { FadeUpGroup } from "../components/FadeUpGroup";

const VARIANTS = {
    privacy: {
        eyebrow: "Privacy Policy",
        title: "How we handle your data",
        icon: ShieldCheck,
        updated: "Last updated: 17 May 2026",
        sections: [
            {
                h: "1. Who we are",
                p: "Scitus.ai is operated by Vyastics Technologies Pvt Ltd. This policy explains what personal data we collect, how we use it, and the rights you have. If you have questions, write to us at hello@scitus.ai.",
            },
            {
                h: "2. What we collect",
                p: "When you submit our demo form we collect your name, company, work email, phone (if shared), industry, products you're interested in, and any free-text message. When you visit scitus.ai we collect standard server logs (IP, user agent, referrer) and product analytics events to measure marketing performance.",
            },
            {
                h: "3. Why we collect it",
                p: "We use this data to schedule and run your demo, to respond to your questions, to send relevant follow-up communications, and to measure how our marketing is performing. We do not sell your data to anyone, ever.",
            },
            {
                h: "4. Where it is stored",
                p: "Lead data is stored on Scitus.ai's managed databases in the region closest to where the request originates. Email is sent via our transactional email provider. Analytics may be processed in additional regions.",
            },
            {
                h: "5. Your rights",
                p: "You may request a copy of, correction to, or deletion of your personal data by emailing hello@scitus.ai. We will respond within 30 days. Where applicable local privacy law (such as GDPR, CCPA, or equivalent) provides additional rights, you may also exercise those rights through the same channel.",
            },
            {
                h: "6. Cookies",
                p: "We use a small number of first-party cookies for analytics and session functionality. Marketing cookies are not loaded until you explicitly consent.",
            },
            {
                h: "7. Changes",
                p: "We may update this policy from time to time. Material changes will be announced on this page with a new \"last updated\" date.",
            },
        ],
    },
    terms: {
        eyebrow: "Terms of Use",
        title: "Terms governing this website",
        icon: ScrollText,
        updated: "Last updated: 17 May 2026",
        sections: [
            {
                h: "1. Acceptance",
                p: "By using scitus.ai you agree to these terms. If you do not agree, please do not use the site. These terms apply to marketing pages and demo requests only — product use is governed by separate Master Service and Data Processing Agreements signed before onboarding.",
            },
            {
                h: "2. Use of content",
                p: "All content on scitus.ai, including text, graphics, logos, product names, and the Scitus.ai cube mark, is the property of Vyastics Technologies Pvt Ltd. You may share links to our pages freely; you may not republish content without permission.",
            },
            {
                h: "3. Trademarks",
                p: "Scitus.ai, Mativex, Vexen, Karya, Vyaahar, Attestra, and Acrium are trademarks of Vyastics Technologies Pvt Ltd. All other marks belong to their respective owners.",
            },
            {
                h: "4. Demo requests",
                p: "When you submit the demo form you authorise us to contact you about Scitus products. You can opt out of further communications at any time by replying \"unsubscribe\" to any email we send.",
            },
            {
                h: "5. Disclaimer",
                p: "The marketing site is provided \"as is\" without warranties of any kind. Product performance commitments, SLAs, and liability terms are governed by your signed Order Form and Master Service Agreement.",
            },
            {
                h: "6. Governing law",
                p: "These terms are governed by the laws of the jurisdiction in which Vyastics Technologies Pvt Ltd is registered, without regard to its conflict of law principles. Disputes shall be resolved through binding arbitration or in the competent courts of that jurisdiction.",
            },
        ],
    },
    dpa: {
        eyebrow: "Data Processing Addendum",
        title: "DPA — summary",
        icon: FileText,
        updated: "Last updated: 17 May 2026",
        sections: [
            {
                h: "1. Scope",
                p: "This Data Processing Addendum (DPA) summarises how Scitus.ai processes personal data on behalf of customers. A full signed DPA is available before onboarding for all paid customers, with provisions appropriate to the personal data being processed and the customer's home jurisdiction.",
            },
            {
                h: "2. Roles",
                p: "For the marketing site and demo requests, Scitus.ai is the data controller. For the Scitus platform itself, Scitus.ai acts as a data processor on behalf of you, the customer, who remains the controller of personal data you upload.",
            },
            {
                h: "3. Subprocessors",
                p: "We maintain a published list of subprocessors (cloud hosting, transactional email, analytics, customer support tooling). Customers are notified 30 days before any new subprocessor is added. The current list is available on request to hello@scitus.ai.",
            },
            {
                h: "4. Security measures",
                p: "We implement industry-standard organisational and technical security measures, including encryption in transit (TLS 1.2+), encryption at rest, role-based access control, audit logging, and annual security reviews. Background checks are performed for all engineering staff.",
            },
            {
                h: "5. Data location and transfers",
                p: "Customer data is stored in the region closest to the customer. Cross-border transfers, where they occur, rely on appropriate transfer mechanisms (such as Standard Contractual Clauses or equivalent recognised safeguards).",
            },
            {
                h: "6. Breach notification",
                p: "We notify customers of confirmed personal-data breaches affecting their data within 72 hours of becoming aware. Notifications include scope, affected categories, mitigations, and our point of contact.",
            },
            {
                h: "7. Audit & support",
                p: "Enterprise customers may audit our security posture annually under NDA. Pen-test summaries and SOC-2-style reports are made available on request.",
            },
        ],
    },
};

export const LegalPage = ({ variant = "privacy" }) => {
    const v = VARIANTS[variant] || VARIANTS.privacy;
    const Icon = v.icon;
    return (
        <section
            className="section"
            id={`legal-${variant}`}
            data-testid={`legal-page-${variant}`}
        >
            <div className="container" style={{ maxWidth: 880 }}>
                <FadeUpGroup>
                    <div className="fade-up">
                        <span className="eyebrow">{v.eyebrow}</span>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 16,
                                marginTop: 18,
                                marginBottom: 12,
                            }}
                        >
                            <span
                                className="icon-square"
                                style={{
                                    width: 52,
                                    height: 52,
                                    background: "var(--accent-soft)",
                                    color: "var(--accent)",
                                    borderRadius: 12,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Icon size={24} />
                            </span>
                            <h1 className="h1" style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
                                {v.title}
                            </h1>
                        </div>
                        <p className="muted" style={{ fontSize: "0.9rem" }}>
                            {v.updated}
                        </p>
                    </div>

                    <div
                        className="card fade-up"
                        style={{ marginTop: 40, padding: "40px 36px" }}
                        data-testid={`legal-content-${variant}`}
                    >
                        {v.sections.map((s) => (
                            <div key={s.h} style={{ marginBottom: 28 }}>
                                <h3
                                    style={{
                                        fontFamily: "'Cabinet Grotesk'",
                                        fontWeight: 700,
                                        fontSize: "1.15rem",
                                        color: "var(--text)",
                                        margin: "0 0 10px 0",
                                    }}
                                >
                                    {s.h}
                                </h3>
                                <p
                                    className="muted"
                                    style={{
                                        margin: 0,
                                        fontSize: "0.96rem",
                                        lineHeight: 1.75,
                                    }}
                                >
                                    {s.p}
                                </p>
                            </div>
                        ))}

                        <hr className="divider" style={{ margin: "28px 0" }} />

                        <p className="muted" style={{ fontSize: "0.88rem", margin: 0 }}>
                            Questions about this document? Email{" "}
                            <a
                                href="mailto:hello@scitus.ai"
                                style={{ color: "var(--accent)" }}
                            >
                                hello@scitus.ai
                            </a>
                            .
                        </p>
                    </div>

                    <div
                        className="fade-up"
                        style={{
                            marginTop: 32,
                            display: "flex",
                            gap: 16,
                            flexWrap: "wrap",
                        }}
                    >
                        <a href="/privacy" className="link-arrow" data-testid="legal-link-privacy">
                            Privacy
                        </a>
                        <a href="/terms" className="link-arrow" data-testid="legal-link-terms">
                            Terms
                        </a>
                        <a href="/dpa" className="link-arrow" data-testid="legal-link-dpa">
                            DPA
                        </a>
                    </div>
                </FadeUpGroup>
            </div>
        </section>
    );
};

export default LegalPage;
